import { supportServiceApiClient } from '@src/api/tenant/supportService/supportServiceApiClient';
import {
  FORM_CONSTANTS,
  SupportServiceForm,
  SupportServiceFormErrors,
  SupportServiceFormProps,
} from '@src/api/types/supportService';
import { useFileUpload } from '@src/hooks/useFileUpload';
import { useScrollToError } from '@src/hooks/useScrollToError';
import { useTagManagement } from '@src/hooks/useTagManagement';
import { Switch } from '@src/ui/elements/form';
import FormField from '@src/ui/elements/form/FormField';
import ImageUploadField from '@src/ui/elements/form/ImageUploadField';
import Input from '@src/ui/elements/form/Input';
import InputCalendar from '@src/ui/elements/form/InputCalendar';
import Select from '@src/ui/elements/form/Select';
import TagField from '@src/ui/elements/form/TagField';
import Textarea from '@src/ui/elements/form/Textarea';
import React, { ChangeEvent, useCallback } from 'react';
import { useWatch } from 'react-hook-form';

const SettingForm: React.FC<SupportServiceFormProps> = ({
  categoryOptions,
  availableTags = [],
  setAvailableTags,
  errors: serverErrors = {} as SupportServiceFormErrors,
  setErrors,
  methods,
}) => {
  const fieldRefs = useScrollToError(serverErrors);
  const onChangeHandler = useCallback(
    (name: keyof SupportServiceForm, value: string | number | number[]) => {
      methods.setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [methods],
  );

  const { handleUpload, handleDelete, isUploading, previewBase64 } = useFileUpload({
    onSuccess: (url) => onChangeHandler('thumbnail_url', url),
    onError: (error) => setErrors?.({ ...serverErrors, thumbnail_url: [error] }),
    apiClientUploadFile: supportServiceApiClient.uploadFile,
    apiClientDeleteFile: supportServiceApiClient.deleteFile,
  });

  const isTopDisplay = useWatch({ control: methods.control, name: 'is_top_display' });

  const {
    newTagName,
    setNewTagName,
    handleNewTag,
    handleAddTag,
    handleRemoveTag,
    tagError,
    selectedTags,
  } = useTagManagement({
    initialTags: methods.getValues('support_service_tags') as number[],
    availableTags,
    // @ts-expect-error Type compatibility between MediaTag and SupportServiceTag
    setAvailableTags,
    onTagsChange: (tags) => onChangeHandler('support_service_tags', tags),
    type: 'supportService',
  });

  return (
    <>
      <FormField
        control={methods.control}
        errors={serverErrors.title}
        name='title'
        label='支援サービス名'
        maxLength={FORM_CONSTANTS.MAX_TITLE_LENGTH}
        component={Input}
        placeholder='支援サービス名'
        componentProps={{
          onChange: (e: ChangeEvent<HTMLInputElement>) => onChangeHandler('title', e.target.value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.title = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.published_at}
        name='published_at'
        label={
          <div className='flex items-end gap-[6px]'>
            <span>公開日</span>
            <span className='text-[10px]'>※09:00公開</span>
          </div>
        }
        component={InputCalendar}
        componentProps={{
          initialValue: methods.getValues('published_at'),
          onChange: (value: string | null) =>
            onChangeHandler('published_at', value === null ? '' : value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.published_at = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.support_service_category_id}
        name='support_service_category_id'
        label='カテゴリ'
        component={Select}
        componentProps={{
          items: categoryOptions?.map((category) => ({
            id: category.id.toString(),
            name: category.name,
          })),
          onSelect: (id: string) => onChangeHandler('support_service_category_id', id),
          initialValue: methods.getValues('support_service_category_id'),
        }}
        fieldRef={(el) => {
          fieldRefs.current.support_service_category_id = el;
        }}
      />

      <TagField
        newTagName={newTagName}
        setNewTagName={setNewTagName}
        tags={
          selectedTags.length > 0
            ? selectedTags
            : (methods.getValues('support_service_tags') as number[])
        }
        onNewTag={handleNewTag}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
        error={tagError}
        availableTags={availableTags}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.description}
        name='description'
        label='概要（description）'
        component={Textarea}
        placeholder='記事の概要を記入してください。検索結果やSNSでシェアしたときに Title とともに表示される概要文です。'
        maxLength={FORM_CONSTANTS.MAX_DESCRIPTION_LENGTH}
        componentProps={{
          onChange: (e: ChangeEvent<HTMLTextAreaElement>) =>
            onChangeHandler('description', e.target.value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.description = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.is_top_display}
        label='上位表示'
        labelPosition='left'
        name='is_top_display'
        component={Switch}
        componentProps={{
          onCheck: () => {
            methods.setValue('is_top_display', true, { shouldValidate: true, shouldDirty: true });
          },
          onUncheck: () => {
            methods.setValue('is_top_display', false, { shouldValidate: true, shouldDirty: true });
          },
          checked: isTopDisplay,
        }}
        fieldRef={(el) => {
          if (el) {
            fieldRefs.current.is_top_display = el;
          }
        }}
      />

      <ImageUploadField
        onUpload={handleUpload}
        onDelete={handleDelete}
        preview={previewBase64 || methods.getValues('thumbnail_url') || undefined}
        isUploading={isUploading}
        errors={serverErrors?.thumbnail}
      />
    </>
  );
};

export default SettingForm;
